/* Proskire — interactive section behaviour.
   Custom elements so everything re-initialises when the theme editor re-renders a section. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;
  const scanReveal = (root = document) => {
    root.querySelectorAll('.psk-reveal:not(.is-in)').forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));
  };
  document.addEventListener('DOMContentLoaded', () => scanReveal());
  document.addEventListener('shopify:section:load', (e) => scanReveal(e.target));

  /* ---------- Slider (hero, testimonials) ---------- */
  class PskSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.index = 0;
      this.viewport = this.track?.parentElement;
      this.fit = () => {
        if (!this.viewport) return;
        if (window.matchMedia('(max-width: 749px)').matches) {
          this.viewport.style.height = this.slides[this.index].offsetHeight + 'px';
        } else {
          this.viewport.style.height = '';
        }
      };
      window.addEventListener('resize', () => this.fit());
      window.addEventListener('load', () => this.fit());
      setTimeout(() => this.fit(), 300);
      if (this.slides.length < 2) return;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i, true)));
      let x0 = null;
      this.track.addEventListener('pointerdown', (e) => { x0 = e.clientX; }, { passive: true });
      this.track.addEventListener('pointerup', (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
      });
      this.delay = parseInt(this.dataset.autoplay || '0', 10) * 1000;
      if (this.delay && !reduced && !window.Shopify?.designMode) {
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.play());
        this.play();
      }
    }
    disconnectedCallback() { this.stop(); }
    play() { this.stop(); if (this.delay) this.timer = setInterval(() => this.go(this.index + 1), this.delay); }
    stop() { clearInterval(this.timer); }
    go(i, user) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.slides.forEach((s, k) => s.setAttribute('aria-hidden', k !== this.index));
      this.dots.forEach((d, k) => d.setAttribute('aria-current', k === this.index));
      this.fit();
      if (user) this.play();
    }
  }
  customElements.get('psk-slider') || customElements.define('psk-slider', PskSlider);

  /* ---------- Tabs (ingredients, bestsellers) ---------- */
  class PskTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.select(i));
        t.addEventListener('mouseenter', () => { if (this.dataset.hover === 'true' && window.matchMedia('(hover: hover)').matches) this.select(i); });
        t.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
          if (!dir) return;
          e.preventDefault();
          const j = (i + dir + this.tabs.length) % this.tabs.length;
          this.select(j); this.tabs[j].focus();
        });
      });
    }
    select(i) {
      this.tabs.forEach((t, k) => { t.setAttribute('aria-selected', k === i); t.tabIndex = k === i ? 0 : -1; });
      this.panels.forEach((p, k) => { p.hidden = k !== i; });
      if (this.dataset.scrollTab === 'true') this.tabs[i].scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduced ? 'auto' : 'smooth' });
    }
  }
  customElements.get('psk-tabs') || customElements.define('psk-tabs', PskTabs);

  /* ---------- Before / after compare ---------- */
  class PskCompare extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('input[type="range"]');
      const set = () => this.style.setProperty('--pos', `${this.range.value}%`);
      this.range.addEventListener('input', set);
      set();
    }
  }
  customElements.get('psk-compare') || customElements.define('psk-compare', PskCompare);

  /* ---------- Count-up numbers ---------- */
  class PskCount extends HTMLElement {
    connectedCallback() {
      const raw = this.textContent.trim();
      const m = raw.match(/^([^\d]*)([\d.,]+)(.*)$/);
      if (!m || reduced || window.Shopify?.designMode) return;
      const [, pre, num, post] = m;
      const target = parseFloat(num.replace(/,/g, ''));
      const decimals = (num.split('.')[1] || '').length;
      const fmt = (v) => pre + v.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: num.includes(',') }) + post;
      this.textContent = fmt(0);
      const run = () => {
        const t0 = performance.now(), dur = 1400;
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / dur), eased = 1 - Math.pow(1 - p, 3);
          this.textContent = fmt(target * eased);
          if (p < 1) requestAnimationFrame(tick); else this.textContent = raw;
        };
        requestAnimationFrame(tick);
      };
      if (!('IntersectionObserver' in window)) { this.textContent = raw; return; }
      const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { o.disconnect(); run(); } }, { threshold: 0.4 });
      o.observe(this);
    }
  }
  customElements.get('psk-count') || customElements.define('psk-count', PskCount);

  /* ---------- Rail arrows ---------- */
  class PskRail extends HTMLElement {
    connectedCallback() {
      const rail = this.querySelector('.psk-rail');
      if (!rail) return;
      const step = () => (rail.firstElementChild?.getBoundingClientRect().width || 300) + 16;
      this.querySelector('[data-prev]')?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.querySelector('[data-next]')?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    }
  }
  customElements.get('psk-rail') || customElements.define('psk-rail', PskRail);

  /* ---------- Quick add to cart (product cards) ---------- */
  class PskAtc extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.btn = this.querySelector('button[type="submit"]');
      this.form?.addEventListener('submit', (e) => this.add(e));
    }
    async add(e) {
      e.preventDefault();
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const label = this.btn.innerHTML;
      this.btn.disabled = true;
      this.btn.textContent = this.dataset.adding || 'Adding…';
      const fd = new FormData(this.form);
      if (cart?.getSectionsToRender) fd.append('sections', cart.getSectionsToRender().map((s) => s.id));
      fd.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        });
        const data = await res.json();
        if (data.status) throw new Error(data.description || data.message);
        if (cart?.renderContents) {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }
        this.btn.textContent = this.dataset.added || 'Added ✓';
        setTimeout(() => { this.btn.innerHTML = label; this.btn.disabled = false; }, 1600);
      } catch (err) {
        this.btn.textContent = err.message || 'Try again';
        setTimeout(() => { this.btn.innerHTML = label; this.btn.disabled = false; }, 2200);
      }
    }
  }
  customElements.get('psk-atc') || customElements.define('psk-atc', PskAtc);

  /* Pack selector: sets the product form quantity (main form, Buy it now, sticky bar) and shows pack totals. */
  class PskPacks extends HTMLElement {
    connectedCallback() {
      this.price = parseInt(this.dataset.price, 10) || 0;
      this.addEventListener('change', (e) => { if (e.target.name?.startsWith('psk-pack')) this.select(e.target); });
      document.addEventListener('variant:change', (e) => {
        const v = e.detail?.variant; if (!v) return;
        this.price = v.price; this.renderTotals(); this.select(this.querySelector('input:checked'), true);
      });
      const hidden = this.closest('product-info, .product')?.querySelector('.product-form__quantity');
      if (hidden) hidden.hidden = true;
    }
    money(cents) {
      if (window.Shopify?.formatMoney) return Shopify.formatMoney(cents, this.dataset.money);
      const n = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return (this.dataset.money || '₹{{amount}}').replace(/\{\{\s*amount\w*\s*\}\}/, n);
    }
    /* Percentage tiers carry data-pct, so their saving has to be recomputed whenever the
       variant price changes; amount tiers keep the fixed data-off in cents. */
    offFor(input, q) {
      const pct = parseFloat(input.dataset.pct);
      if (!isNaN(pct)) return Math.round((this.price * q * pct) / 100);
      return +input.dataset.off || 0;
    }
    renderTotals() {
      this.querySelectorAll('.ppk__opt').forEach((opt) => {
        const i = opt.querySelector('input'); const q = +i.value;
        const off = this.offFor(i, q);
        const t = opt.querySelector('[data-ppk-total]'); const f = opt.querySelector('[data-ppk-full]');
        if (t) t.textContent = this.money(this.price * q - off);
        if (f) f.textContent = this.money(this.price * q);
      });
    }
    select(input, silent) {
      if (!input) return;
      const q = +input.value; const off = this.offFor(input, q);
      this.querySelectorAll('.ppk__opt').forEach((o) => o.classList.toggle('is-selected', o.contains(input)));
      const scope = this.closest('product-info') || document;
      scope.querySelectorAll('input[name="quantity"]').forEach((qi) => {
        qi.value = q; qi.dispatchEvent(new Event('change', { bubbles: true }));
      });
      document.querySelectorAll('[data-satc-qty]').forEach((qi) => { qi.value = q; });
      if (q > 1) document.dispatchEvent(new CustomEvent('qb:tier-select', { detail: { qty: q, totalPrice: this.price * q - off } }));
      else document.dispatchEvent(new CustomEvent('qb:tier-clear'));
    }
  }
  customElements.get('psk-packs') || customElements.define('psk-packs', PskPacks);
})();

/* Safety net: the theme's announcement marquee stays hidden until web fonts report ready.
   If that never fires, trigger its own relayout (it listens to resize). */
window.addEventListener('load', () => setTimeout(() => {
  if (document.querySelector('[data-marquee-track]:not(.is-ready)')) window.dispatchEvent(new Event('resize'));
}, 2500));
